import React, { useCallback, useEffect, useMemo } from 'react'
import { useDispatch } from 'react-redux'
import { Col, Form, Row, Spin } from 'antd'
import PropTypes from 'prop-types'

import {
  DirtyPromptForm,
  FormButtons,
  FormTextareaInput,
} from '../../../common/components/sections'
import { PERMISSIONS } from '../../../common/constants'
import { showErrorNotification } from '../../../common/helpers'
import { useFormDirty, useUserHasPermissions } from '../../../common/hooks'
import { idPropType } from '../../../common/prop-types'

import { useUpdateDishTypeNotesMutation } from '../../hooks'
import { setDishTypePageIsDirty } from '../../redux/actions'

import { dishTypeNotesFormInitialValues } from './initial-values'

const TEXTAREA_MAX_ROWS = 100

const DishTypeNotesForm = ({ id, notes }) => {
  const dispatch = useDispatch()
  const [form] = Form.useForm()
  const { isDirty, setToDirty, resetForm } = useFormDirty(form)

  const isFormEnabled = useUserHasPermissions([
    PERMISSIONS.DISH_TYPE.UPDATE_NOTES,
  ])

  const [updateDishTypeNotes, { isLoading: isSaveLoading, error: saveError }] =
    useUpdateDishTypeNotesMutation()

  const initialValues = useMemo(
    () => dishTypeNotesFormInitialValues({ notes }),
    [notes],
  )

  const handleSaveData = useCallback(
    data => updateDishTypeNotes(id, data),
    [id, updateDishTypeNotes],
  )

  useEffect(() => {
    if (saveError) {
      showErrorNotification({
        title: 'Dish Type notes error',
        description: saveError,
      })
    }
  }, [saveError])

  useEffect(() => {
    resetForm()
  }, [notes, resetForm])

  useEffect(() => {
    dispatch(setDishTypePageIsDirty({ isNotesDirty: isDirty }))
  }, [dispatch, isDirty])

  return isSaveLoading ? (
    <Spin />
  ) : (
    <DirtyPromptForm
      isDirty={isDirty}
      form={form}
      initialValues={initialValues}
      name="dishTypeNotesForm"
      onFinish={handleSaveData}
      autoComplete="off"
      onFieldsChange={setToDirty}
    >
      <Row gutter={[16]}>
        <Col span={12}>
          <FormTextareaInput
            disabled={!isFormEnabled}
            name="notes"
            label="Notes"
            labelAbove
            placeholder="Input notes..."
            rows={4}
            style={{
              resize: 'vertical',
              maxHeight: `${TEXTAREA_MAX_ROWS * 24}px`,
            }}
          />
        </Col>
      </Row>
      <FormButtons
        permissions={[PERMISSIONS.DISH_TYPE.UPDATE_NOTES]}
        isDisabled={!isDirty}
        onReset={resetForm}
      />
    </DirtyPromptForm>
  )
}

DishTypeNotesForm.propTypes = {
  id: idPropType.isRequired,
  notes: PropTypes.string,
}

DishTypeNotesForm.defaultProps = {
  notes: undefined,
}

export default DishTypeNotesForm
